import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Shield, ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Terms = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" asChild className="mb-6">
          <Link to="/">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Link>
        </Button>

        <h1 className="text-3xl font-bold mb-6">Legal</h1>

        <Tabs defaultValue="terms" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="terms" className="flex items-center gap-2">
              <ScrollText className="h-4 w-4" />
              <span className="hidden sm:inline">Terms of Service</span>
              <span className="sm:hidden">Terms</span>
            </TabsTrigger>
            <TabsTrigger value="privacy" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Privacy Policy</span>
              <span className="sm:hidden">Privacy</span>
            </TabsTrigger>
            <TabsTrigger value="conditions" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Terms & Conditions</span>
              <span className="sm:hidden">T&C</span>
            </TabsTrigger>
          </TabsList>

          {/* End User Agreement / Terms of Service */}
          <TabsContent value="terms">
            <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6 text-muted-foreground">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground">END USER AGREEMENT (TERMS OF SERVICE)</h2>
                <p className="text-sm">Effective Date: January 01, 2026</p>
                <p className="text-sm">Company: Returnify LLC</p>
              </div>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">1. Acceptance of Terms</h3>
                <p>
                  By accessing or using the Returnify mobile application, website, or related services (collectively, the "Service"), you agree to be bound by this End User Agreement ("Agreement"). If you do not agree, you may not use the Service.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">2. Description of Service</h3>
                <p>
                  Returnify is a technology-enabled platform that allows users to schedule package pickup services for retail and carrier returns, including but not limited to Amazon, UPS, USPS, FedEx, and Kohl's.
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Returnify is not affiliated with, endorsed by, or sponsored by Amazon, UPS, USPS, FedEx, Kohl's, or any other third-party retailer or carrier.</li>
                  <li>Returnify does not guarantee that any return will be accepted by a third party.</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">3. Eligibility</h3>
                <p>You must be at least eighteen (18) years old to use the Service.</p>
                <p>Returnify does not permit use by minors.</p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">4. Independent Contractors</h3>
                <p>
                  All pickup and delivery services are performed by independent contractors, not employees or agents of Returnify.
                </p>
                <p>
                  Returnify does not control the manner or means by which contractors perform services and is not responsible for contractor conduct beyond facilitating the pickup coordination.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">5. User Responsibilities</h3>
                <p>You agree to:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Provide accurate pickup address and contact information</li>
                  <li>Ensure packages are properly sealed, labeled, and compliant with carrier rules</li>
                  <li>Upload accurate and valid QR codes and package photos</li>
                  <li>Make packages available during scheduled pickup windows</li>
                  <li>Ensure pickup locations are safe and accessible</li>
                </ul>
                <p className="mt-2">
                  You acknowledge that failure to comply may result in a failed pickup, which may count toward usage limits.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">6. User Content (Photos & QR Codes)</h3>
                <p>
                  Users may upload package photos and QR codes. Contractors may upload pickup and drop-off confirmation photos.
                </p>
                <p>
                  By uploading content, you grant Returnify a non-exclusive, royalty-free license to use such content solely for operational, support, dispute resolution, and quality assurance purposes.
                </p>
                <p>You represent that you have the right to upload all content submitted.</p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">7. Subscriptions, Payments & No Refund Policy</h3>
                <p>Returnify operates on a subscription-based model, billed in advance through Stripe.</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li><strong>All fees are non-refundable</strong></li>
                  <li>No refunds will be issued for unused pickups, failed pickups, cancellations, or dissatisfaction</li>
                  <li>Subscription plans may include usage limits</li>
                  <li>Unused pickups do not roll over unless explicitly stated</li>
                </ul>
                <p className="mt-2">
                  You may cancel or pause your subscription through your account dashboard, but cancellation does not entitle you to a refund.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">8. Failed or Missed Pickups</h3>
                <p>A pickup may be deemed failed due to:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>User unavailability</li>
                  <li>Incorrect or missing QR codes</li>
                  <li>Improper packaging</li>
                  <li>Unsafe or inaccessible pickup locations</li>
                  <li>Prohibited items</li>
                </ul>
                <p className="mt-2">Returnify is not obligated to reattempt failed pickups.</p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">9. Risk of Loss</h3>
                <p>Returnify is not a carrier.</p>
                <p>Title and risk of loss remain with the user at all times, including during pickup and drop-off.</p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">10. Limitation of Liability</h3>
                <p>To the maximum extent permitted by California law, Returnify shall not be liable for:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Lost, delayed, damaged, or rejected packages</li>
                  <li>Third-party carrier actions or decisions</li>
                  <li>Indirect, incidental, or consequential damages</li>
                </ul>
                <p className="mt-2">
                  Returnify's total liability shall not exceed the amount paid by you to Returnify in the thirty (30) days preceding the event giving rise to the claim.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">11. Indemnification</h3>
                <p>You agree to indemnify and hold harmless Returnify LLC from any claims, damages, losses, or expenses arising out of:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Your misuse of the Service</li>
                  <li>Your violation of this Agreement</li>
                  <li>Your uploaded content</li>
                  <li>Your failure to comply with carrier requirements</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">12. Termination</h3>
                <p>
                  Returnify may suspend or terminate your access at any time, with or without notice, for violation of these terms or misuse of the Service.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">13. Governing Law</h3>
                <p>
                  This Agreement is governed by the laws of the State of California, without regard to conflict-of-law principles.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">14. Changes to Terms</h3>
                <p>
                  Returnify may modify this Agreement at any time. Continued use of the Service constitutes acceptance of the updated terms.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">15. Contact Information</h3>
                <p>Returnify LLC</p>
                <p>
                  📧 Support Email:{" "}
                  <a href="mailto:returnify.help@gmail.com" className="text-primary hover:underline">
                    returnify.help@gmail.com
                  </a>
                </p>
              </section>
            </div>
          </TabsContent>

          {/* Privacy Policy */}
          <TabsContent value="privacy">
            <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6 text-muted-foreground">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground">PRIVACY POLICY</h2>
                <p className="text-sm">Effective Date: January 01, 2026</p>
                <p className="text-sm">Company: Returnify LLC</p>
              </div>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">1. Information We Collect</h3>
                <p>We may collect the following categories of information:</p>
                
                <h4 className="text-lg font-medium mt-4 mb-2 text-foreground">Personal Information</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Pickup and return addresses</li>
                </ul>

                <h4 className="text-lg font-medium mt-4 mb-2 text-foreground">Usage & Operational Data</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Pickup history</li>
                  <li>Subscription details</li>
                  <li>Uploaded photos and QR codes</li>
                  <li>App interaction data</li>
                </ul>

                <h4 className="text-lg font-medium mt-4 mb-2 text-foreground">Device & Technical Data</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>IP address</li>
                  <li>Browser type</li>
                  <li>Device identifiers</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">2. How We Use Information</h3>
                <p>We use personal information to:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Provide and manage pickup services</li>
                  <li>Process subscriptions and payments</li>
                  <li>Coordinate with independent contractors</li>
                  <li>Provide customer support</li>
                  <li>Improve service quality</li>
                  <li>Prevent fraud and misuse</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">3. Payments</h3>
                <p>All payments are processed by Stripe.</p>
                <p>Returnify does not store full payment card information.</p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">4. Sharing of Information</h3>
                <p>We may share information with:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Independent contractors performing pickups</li>
                  <li>Service providers (e.g., Stripe, mapping services, hosting providers)</li>
                  <li>Legal authorities if required by law</li>
                </ul>
                <p className="mt-2 font-medium">Returnify does not sell personal information.</p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">5. California Privacy Rights (CCPA)</h3>
                <p>California residents have the right to:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Request access to personal information</li>
                  <li>Request deletion of personal information</li>
                  <li>Request information about data usage</li>
                  <li>Not be discriminated against for exercising privacy rights</li>
                </ul>
                <p className="mt-2">
                  Requests may be submitted to:{" "}
                  <a href="mailto:returnify.help@gmail.com" className="text-primary hover:underline">
                    📧 returnify.help@gmail.com
                  </a>
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">6. Data Retention</h3>
                <p>We retain personal information only as long as necessary to:</p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Provide services</li>
                  <li>Resolve disputes</li>
                  <li>Comply with legal and tax obligations</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">7. Data Security</h3>
                <p>
                  We implement reasonable administrative, technical, and physical safeguards to protect personal information. However, no system is 100% secure.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">8. Children's Privacy</h3>
                <p>
                  Returnify does not knowingly collect or permit use by individuals under 18 years of age.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">9. Changes to This Policy</h3>
                <p>
                  We may update this Privacy Policy periodically. Updates will be posted within the app or on the website.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">10. Contact Us</h3>
                <p>Returnify LLC</p>
                <p>
                  📧{" "}
                  <a href="mailto:returnify.help@gmail.com" className="text-primary hover:underline">
                    returnify.help@gmail.com
                  </a>
                </p>
              </section>
            </div>
          </TabsContent>

          {/* Original Terms and Conditions */}
          <TabsContent value="conditions">
            <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6 text-muted-foreground">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground">TERMS AND CONDITIONS</h2>
                <p className="text-sm">Effective Date: January 01, 2026</p>
                <p className="text-sm">Company: Returnify LLC</p>
              </div>

              <p>
                Returnify LLC is a service, website, and mobile application. This Agreement and all policies on the Returnify LLC platforms set forth the terms upon which Returnify LLC offers you access to and use of its websites, services, applications, and tools (collectively, the "Services"). All policies and related agreements between you and Returnify LLC are incorporated into this Agreement.
              </p>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">ACCEPTANCE OF TERMS</h3>
                <p>
                  By accessing or using the website located at returnify.app, the Returnify LLC mobile application, or any related services (collectively, the "Returnify LLC Platforms"), you agree to be bound by these Terms and Conditions and the Returnify LLC Privacy Policy. These Terms may be updated from time to time. Continued use of the Returnify LLC Platforms after changes are posted constitutes acceptance of those changes. You will be notified of material updates to the Privacy Policy as required by law.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">ABOUT RETURNIFY LLC AND USE OF SERVICES</h3>
                <p>
                  Returnify LLC provides an electronic platform and physical courier services to assist members with returning purchased items. By registering for and using the Returnify LLC Platforms, you contract directly with Returnify LLC. Your use of the Services is at your own risk.
                </p>
                <p>
                  Returnify LLC operates by accessing your online purchase history through your email or manual uploads, as described in the Returnify LLC Privacy Policy. Once purchase history is uploaded, Returnify LLC may initiate the return process on your behalf.
                </p>
                <p>
                  By using the Services, you expressly authorize Returnify LLC to access your purchase history and perform return-related actions for you.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">USER ACCOUNTS AND USE</h3>
                <p>
                  You are responsible for maintaining the confidentiality of your login credentials and account information ("Confidential Information"). You are responsible for all activity occurring under your account, whether authorized or unauthorized.
                </p>
                <p>
                  You must ensure your account information is accurate and current. If you believe your account security has been compromised, you must promptly update your credentials and notify Returnify LLC at{" "}
                  <a href="mailto:returnify.help@gmail.com" className="text-primary hover:underline">
                    returnify.help@gmail.com
                  </a>.
                </p>
                <p>
                  Returnify LLC reserves the right, at its sole discretion and without notice, to limit, suspend, or terminate accounts, refuse service, remove content, or take legal or technical action if misuse, abuse, or policy violations are suspected.
                </p>
                <p>
                  Inactive or unconfirmed accounts may be cancelled. Returnify LLC may modify or discontinue Services at any time.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">POLICY ENFORCEMENT</h3>
                <p>
                  Returnify LLC may consider account history and circumstances when enforcing policies and may exercise discretion in enforcement decisions.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">PURCHASES AND SERVICE REQUESTS</h3>
                <p>
                  Use of the Returnify LLC Platforms constitutes an offer to use Services for personal, non-commercial purposes. Returnify LLC reserves the right to limit, cancel, or refuse any service request that violates these Terms or applicable law.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">REFUSAL OF SERVICES</h3>
                <p>
                  Returnify LLC may refuse to process Services at its sole discretion, including but not limited to requests involving unreturnable items or items not in original condition. Returnify LLC is not liable for refusal of Services.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">RETURNS AND REFUNDS</h3>
                <p>
                  Refund amounts are estimates only and may vary based on merchant policies, state laws, shipping fees, or other applicable costs. Returnify LLC makes no guarantees regarding refund amounts or timing.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">PAYMENT INFORMATION</h3>
                <p>
                  Payment information is processed securely by third-party payment processors. Returnify LLC may use third parties to store or process payment data and does not directly store full credit card numbers.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">CHANGES TO SERVICES OR TERMS</h3>
                <p>
                  Returnify LLC may modify, suspend, or discontinue any aspect of the Platforms or Services at any time without notice. Continued use constitutes acceptance of updated Terms.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">AUTHORIZATION TO CONTACT YOU</h3>
                <p>
                  You consent to receive calls, emails, and text messages (including automated or prerecorded messages) from Returnify LLC for account-related, service, marketing, legal, or enforcement purposes. Standard carrier charges may apply.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">THIRD-PARTY LINKS AND ADVERTISING</h3>
                <p>
                  The Platforms may contain links to third-party websites or advertisements. Returnify LLC does not control or endorse third-party content and is not responsible for their availability, practices, or content.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">INTELLECTUAL PROPERTY</h3>
                <p>
                  All content, software, and materials on the Returnify LLC Platforms are owned or licensed by Returnify LLC and protected by applicable intellectual property laws. Content may be accessed solely for personal, non-commercial use.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">INTELLECTUAL PROPERTY INFRINGEMENT</h3>
                <p>
                  Claims of copyright, trademark, or intellectual property infringement must be submitted to{" "}
                  <a href="mailto:returnify.help@gmail.com" className="text-primary hover:underline">
                    returnify.help@gmail.com
                  </a>{" "}
                  and include all legally required information.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">PROHIBITED CONDUCT</h3>
                <p>You agree not to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Use automated tools to access or scrape the Platforms without permission</li>
                  <li>Introduce malware or harmful code</li>
                  <li>Reverse engineer or attempt to extract source code</li>
                  <li>Submit false or fraudulent service requests</li>
                  <li>Violate applicable laws or third-party rights</li>
                  <li>Interfere with platform security or operations</li>
                </ul>
                <p className="mt-4">
                  You represent that you are at least 18 years old and legally capable of entering into binding contracts.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">DISCLAIMERS AND LIMITATION OF LIABILITY</h3>
                <p>
                  Services are provided "AS IS" and "AS AVAILABLE." To the fullest extent permitted by law, Returnify LLC disclaims all warranties and shall not be liable for indirect, incidental, consequential, punitive, or special damages. Nothing limits liability for death or personal injury caused by negligence where prohibited by law.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">INDEMNIFICATION</h3>
                <p>
                  You agree to indemnify and hold harmless Returnify LLC and its affiliates from claims arising out of your breach of these Terms or misuse of the Services.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">GOVERNING LAW</h3>
                <p>
                  These Terms are governed by the laws of the United States and the State of California, without regard to conflict-of-law principles.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">DISPUTE RESOLUTION AND ARBITRATION</h3>
                <p>
                  Disputes must first be addressed through good-faith informal resolution for at least thirty (30) days. Unresolved disputes shall be resolved through binding arbitration administered by the American Arbitration Association in the Los Angeles County in the State of California.
                </p>
                <p>
                  You waive the right to a jury trial and agree that disputes may only be brought in an individual capacity. Class actions and representative proceedings are waived to the fullest extent permitted by law.
                </p>
                <p>
                  Certain claims (criminal matters, intellectual property, injunctive relief) may be brought in state or federal courts located in California, at Returnify LLC's election.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">CALIFORNIA CONSUMER PRIVACY RIGHTS (CCPA / CPRA)</h3>
                <p>
                  If you are a California resident, you have the following rights under the California Consumer Privacy Act as amended by the California Privacy Rights Act (CPRA):
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Right to Know what personal information is collected, used, disclosed, or shared</li>
                  <li>Right to Delete personal information, subject to legal exceptions</li>
                  <li>Right to Correct inaccurate personal information</li>
                  <li>Right to Opt Out of the sale or sharing of personal information</li>
                  <li>Right to Limit the use of sensitive personal information</li>
                  <li>Right to Non-Discrimination for exercising your privacy rights</li>
                </ul>
                <p className="mt-4">
                  You may exercise these rights by contacting{" "}
                  <a href="mailto:returnify.help@gmail.com" className="text-primary hover:underline">
                    returnify.help@gmail.com
                  </a>
                  . Requests will be verified as required by law. Authorized agents may submit requests on your behalf where permitted.
                </p>
                <p>
                  Additional details regarding data practices are provided in the Returnify LLC Privacy Policy.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold mb-3 text-foreground">WAIVER AND SEVERABILITY</h3>
                <p>
                  Failure to enforce any provision does not constitute a waiver. If any provision is found unenforceable, the remainder shall remain in full force and effect.
                </p>
              </section>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Terms;
